import dayjs from "dayjs"
import { useCallback, useReducer, useEffect, useRef } from "react"
import { DATE_FORMAT } from "src/utils/constants"
import { calculateMinWidth } from "src/utils/table-render-functions"

const initialState = {
    loading: false,
    rows: [],
    table: [],
    ultimoEvento: { data_referencia: "", evento_credito: "", score_evento: "" },
    alert: { open: false, message: "", type: "" },
    openModal: false,
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload }
        case "SET_ROWS":
            return { ...state, rows: action.payload }
        case "SET_TABLE":
            return { ...state, table: action.payload }
        case "SET_ULTIMO_EVENTO":
            return { ...state, ultimoEvento: action.payload }
        case "SET_ALERT":
            return { ...state, alert: { ...state.alert, ...action.payload } }
        case "CLOSE_ALERT":
            return { ...state, alert: { ...state.alert, open: false } }
        case "SET_MODAL":
            return { ...state, openModal: action.payload }
        default:
            return state
    }
}

const ordenaPorDataReferencia = (arr, order = "asc") => {
    return [...arr].sort((a, b) => {
        const dateA = a?.DataReferencia
            ? new Date(a.DataReferencia)
            : new Date(0)
        const dateB = b?.DataReferencia
            ? new Date(b.DataReferencia)
            : new Date(0)
        return order === "desc" ? dateB - dateA : dateA - dateB
    })
}

const getCetipConsulta = (cetipIsin, dadosEmpresa) => {
    if (dadosEmpresa?.CETIP) return dadosEmpresa.CETIP
    if (dadosEmpresa?.cetip) return dadosEmpresa.cetip

    if (cetipIsin?.CETIP) return cetipIsin.CETIP
    if (cetipIsin?.cetip) return cetipIsin.cetip

    if (typeof cetipIsin === "string") return cetipIsin

    return ""
}

export const useUltimoEventoCreditoEmissao = (
    emissaoService,
    cetipIsin,
    dadosEmpresa
) => {
    const [state, dispatch] = useReducer(reducer, initialState)
    const cetipConsulta = getCetipConsulta(cetipIsin, dadosEmpresa)
    const loadedRef = useRef(cetipConsulta)

    useEffect(() => {
        if (loadedRef.current === cetipConsulta) return
        loadedRef.current = cetipConsulta

        dispatch({ type: "SET_ROWS", payload: [] })
        dispatch({ type: "CLOSE_ALERT" })
    }, [cetipConsulta])

    const buildTable = useCallback(() => {
        dispatch({
            type: "SET_TABLE",
            payload: [
                {
                    cells: [
                        `CETIP: ${dadosEmpresa?.CETIP || cetipConsulta || ""}`,
                        `ISIN: ${dadosEmpresa?.ISIN || ""}`,
                    ],
                },
            ],
        })
    }, [cetipConsulta, dadosEmpresa])

    const loadTableRows = useCallback(async () => {
        if (!cetipConsulta || cetipConsulta.length <= 4) {
            dispatch({ type: "SET_MODAL", payload: true })
            return
        }

        buildTable()
        dispatch({ type: "SET_LOADING", payload: true })

        try {
            const response = await emissaoService.eventoCreditoEmissao(
                "get",
                `?CETIP=${cetipConsulta.toUpperCase()}`
            )
            const { results } = response.body

            if (!results || results.length === 0) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Não há dados para o CETIP consultado.",
                        type: "warning",
                    },
                })
                dispatch({ type: "SET_ROWS", payload: [] })
            } else {
                dispatch({ type: "CLOSE_ALERT" })
                
                const rows = ordenaPorDataReferencia(results, "desc").map(
                    (obj, index) => ({ ...obj, id: index })
                )
                dispatch({ type: "SET_ROWS", payload: rows })
            }
            dispatch({ type: "SET_MODAL", payload: true })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [cetipConsulta, emissaoService, buildTable])

    const deleteRow = useCallback(
        async (row) => {
            const { ID } = row
            dispatch({ type: "SET_LOADING", payload: true })
            try {
                await emissaoService.eventoCreditoEmissao("delete", ID)
                await loadTableRows()
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, loadTableRows]
    )

    const addRow = useCallback(async () => {
        let lastItem = state.rows[0]
        if (!lastItem) {
            lastItem = {
                CETIP: cetipConsulta,
                DataReferencia: dayjs().format(DATE_FORMAT),
                EventoCreditoEmissao: "N/A",
                ScoreEvento: 0,
            }
        }

        dispatch({ type: "SET_LOADING", payload: true })
        try {
            await emissaoService.eventoCreditoEmissao("post", {
                ...lastItem,
                CETIP: lastItem?.CETIP ?? cetipConsulta,
            })
            await loadTableRows()
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [state.rows, cetipConsulta, emissaoService, loadTableRows])

    const updateRow = useCallback(
        async (newRow) => {
            const { ID } = newRow
            dispatch({ type: "SET_LOADING", payload: true })
            try {
                await emissaoService.eventoCreditoEmissao("patch", ID, newRow)
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
            return newRow
        },
        [emissaoService]
    )

    const setAlert = useCallback(
        (payload) => dispatch({ type: "SET_ALERT", payload }),
        []
    )
    const closeAlert = useCallback(() => dispatch({ type: "CLOSE_ALERT" }), [])

    return {
        state: {
            ...state,
            minWidthEvento:
                calculateMinWidth(state.rows, "EventoCreditoEmissao") / 2,
        },
        actions: {
            loadTableRows,
            deleteRow,
            addRow,
            updateRow,
            setAlert,
            closeAlert,
            setRows: (rows) => dispatch({ type: "SET_ROWS", payload: rows }),
            closeModal: () => dispatch({ type: "SET_MODAL", payload: false }),
        },
    }
}